import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API } from '@/App';
import { reopenConsent } from '@/lib/consent';

const Footer = () => {
  const { t } = useTranslation();
  const [contactInfo, setContactInfo] = useState({
    general_email: 'info@bess-invest.com',
    phone: '+49 123 456 789',
    address: 'Musterstraße 123, 80331 München, Deutschland',
    company_name: 'Speier Solarbetriebe',
    facebook_url: null,
    linkedin_url: null,
    twitter_url: null,
    instagram_url: null
  });

  useEffect(() => {
    fetchContactInfo();
  }, []);

  const fetchContactInfo = async () => {
    try {
      const response = await axios.get(`${API}/settings/contact`);
      if (response.data) {
        setContactInfo(response.data);
      }
    } catch (error) {
      console.error('Error fetching contact info:', error);
    }
  };

  return (
    <footer className="bg-slate-900 text-white py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">BE</span>
              </div>
              <span className="text-xl font-bold">BESS Invest</span>
            </div>
            <p className="text-slate-400 text-sm">
              {t('footer.tagline')}
            </p>
            
            {/* Social Media Links */}
            {(contactInfo.facebook_url || contactInfo.linkedin_url || contactInfo.twitter_url || contactInfo.instagram_url) && (
              <div className="flex space-x-3 mt-4">
                {contactInfo.facebook_url && (
                  <a href={contactInfo.facebook_url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-green-400 transition-colors">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  </a>
                )}
                {contactInfo.linkedin_url && (
                  <a href={contactInfo.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-green-400 transition-colors">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  </a>
                )}
                {contactInfo.twitter_url && (
                  <a href={contactInfo.twitter_url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-green-400 transition-colors">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
                  </a>
                )}
                {contactInfo.instagram_url && (
                  <a href={contactInfo.instagram_url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-green-400 transition-colors">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                  </a>
                )}
              </div>
            )}
          </div>

          <div>
            <h3 className="font-semibold mb-4">{t('footer.linksTitle')}</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/bess" className="text-slate-400 hover:text-green-400 transition-colors">
                  {t('footer.home')}
                </Link>
              </li>
              <li>
                <Link to="/bess/projects" className="text-slate-400 hover:text-green-400 transition-colors">
                  {t('footer.projects')}
                </Link>
              </li>
              <li>
                <Link to="/bess/info" className="text-slate-400 hover:text-green-400 transition-colors">
                  {t('footer.info')}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-4">{t('footer.legalTitle')}</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/bess/impressum" className="text-slate-400 hover:text-green-400 transition-colors">
                  {t('footer.impressum')}
                </Link>
              </li>
              <li>
                <Link to="/bess/datenschutz" className="text-slate-400 hover:text-green-400 transition-colors">
                  {t('footer.privacy')}
                </Link>
              </li>
              <li>
                <button type="button" onClick={reopenConsent} className="text-slate-400 hover:text-green-400 transition-colors" data-testid="footer-cookie-settings">
                  {t('consent.settings')}
                </button>
              </li>
              <li>
                <a href="#" className="text-slate-400 hover:text-green-400 transition-colors">
                  {t('footer.terms')}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-4">{t('footer.contactTitle')}</h3>
            <ul className="space-y-2 text-sm text-slate-400">
              <li>{t('footer.email')}: {contactInfo.general_email}</li>
              <li>{t('footer.phone')}: {contactInfo.phone}</li>
              <li className="whitespace-pre-line">{contactInfo.address}</li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm text-slate-400">
          <p>{t('footer.copyright', { company: contactInfo.company_name })}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
